/**
 * Navigation entries, one per feature (docs/08 §2: shared file with a line per feature).
 * `primary` entries form the tablet bottom bar (5 + "Altro"); `roles` (when set) restricts the entry
 * and the pages under its path (`canAccessPath`), and so does `quantities` for `readonly` without
 * `readonlyShowsQuantities`. The API checks the roles again (RBAC docs/08 §6): hiding an entry is
 * only the UI side.
 */
import type { Role } from 'core';
import {
  Archive,
  ArrowLeftRight,
  Boxes,
  CalendarRange,
  ClipboardCheck,
  ClipboardList,
  Download,
  History,
  LayoutDashboard,
  ListChecks,
  MessageSquareText,
  RefreshCw,
  Settings,
  TrafficCone,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { backTarget } from '@/lib/back';
import { todayRome } from '@/lib/time';

export type NavGroup = 'operativo' | 'registri' | 'gestione';

export interface NavItem {
  key: string;
  label: string;
  /** Static path or a function for paths that depend on today's date. */
  to: string | (() => string);
  icon: LucideIcon;
  group: NavGroup;
  primary?: boolean;
  roles?: readonly Role[];
  /** Only for who sees the quantities (`seesQuantities`): the API answers 403 to the others. */
  quantities?: boolean;
}

const MANAGERS: readonly Role[] = ['admin', 'supervisor'];
/** Everyone but the point managers (`punto` sees only its operational pages). */
const NOT_POINT: readonly Role[] = ['admin', 'supervisor', 'readonly'];

export const NAV_GROUP_LABELS: Record<NavGroup, string> = {
  operativo: 'Operativo',
  registri: 'Registri e documenti',
  gestione: 'Gestione',
};

export const NAV_ITEMS: readonly NavItem[] = [
  {
    key: 'dashboard',
    label: 'Cruscotto',
    to: '/',
    icon: LayoutDashboard,
    group: 'operativo',
    primary: true,
  },
  {
    key: 'movements',
    label: 'Movimenti',
    to: '/movimenti',
    icon: ArrowLeftRight,
    group: 'operativo',
    primary: true,
  },
  {
    key: 'reports',
    label: 'Report',
    to: '/report',
    icon: ClipboardList,
    group: 'operativo',
    primary: true,
  },
  {
    key: 'traffic',
    label: 'Semaforo',
    to: '/semaforo',
    icon: TrafficCone,
    group: 'operativo',
    primary: true,
  },
  {
    key: 'closure',
    label: 'Chiusura',
    to: () => `/chiusura/${todayRome()}`,
    icon: ClipboardCheck,
    group: 'operativo',
    primary: true,
  },
  {
    key: 'stock',
    label: 'Giacenze',
    to: '/giacenze',
    icon: Boxes,
    group: 'operativo',
    // `punto` sees its own point only (the API returns `scope: 'point'`)
  },
  {
    key: 'planning',
    label: 'Pianificazione',
    to: '/pianificazione',
    icon: CalendarRange,
    group: 'operativo',
    roles: NOT_POINT,
  },
  {
    key: 'checklist',
    label: 'Checklist',
    to: () => `/checklist/${todayRome()}`,
    icon: ListChecks,
    group: 'operativo',
    roles: NOT_POINT,
  },
  {
    key: 'archive',
    label: 'Archivio',
    to: '/archivio',
    icon: Archive,
    group: 'registri',
    // `punto` sees the documents of its own point (the API filters them)
    quantities: true,
  },
  {
    key: 'texts',
    label: 'Scheda staff',
    to: '/scheda',
    icon: MessageSquareText,
    group: 'registri',
  },
  {
    key: 'export',
    label: 'Esporta',
    to: '/esporta',
    icon: Download,
    group: 'registri',
    roles: MANAGERS,
  },
  {
    key: 'audit',
    label: 'Audit log',
    to: '/audit',
    icon: History,
    group: 'registri',
    roles: MANAGERS,
  },
  {
    key: 'settings',
    label: 'Impostazioni',
    to: '/impostazioni/generali',
    icon: Settings,
    group: 'gestione',
    roles: ['admin'],
  },
  { key: 'sync', label: 'Sincronizzazione', to: '/sync', icon: RefreshCw, group: 'gestione' },
];

/**
 * A link that keeps the reference instant of the page (`?at=`, "Situazione al"): from the dashboard
 * at a past time the stock opens at the same time, not in real time.
 */
export function withAtParam(path: string, at: string | null | undefined): string {
  if (!at) return path;
  return `${path}${path.includes('?') ? '&' : '?'}at=${encodeURIComponent(at)}`;
}

/** First segment of a path ("/movimenti/12?torna=…" → "/movimenti"); "/" for the dashboard. */
function pathBase(path: string): string {
  const pathname = path.split(/[?#]/, 1)[0] ?? path;
  return pathname === '/' ? '/' : `/${pathname.split('/')[1]}`;
}

/**
 * Link of an entry. On a page opened from a list (`?torna=`: a movement from the filtered register,
 * the archive or the audit log) the entry of that list's section goes back to it, filters, search
 * and sort included; the other entries keep their own path.
 */
export function navPath(item: NavItem, location?: { search: string }): string {
  const path = typeof item.to === 'function' ? item.to() : item.to;
  const back = location ? backTarget(location.search) : null;
  return back && pathBase(back) === pathBase(path) ? back : path;
}

/** First path segment of an entry ("/impostazioni/generali" → "/impostazioni"); "/" for the dashboard. */
export function navBase(item: NavItem): string {
  return pathBase(navPath(item));
}

export function isNavItemActive(pathname: string, item: NavItem): boolean {
  const base = navBase(item);
  if (base === '/') return pathname === '/';
  return pathname === base || pathname.startsWith(`${base}/`);
}

/**
 * Whether the role sees the quantities, as `canSeeQuantities` of the API (rbac.ts): `readonly` per
 * `readonlyShowsQuantities` (from `GET /settings`; undefined while unknown), `punto` those of its
 * own point, admin/supervisor always.
 */
export function seesQuantities(
  role: Role,
  readonlyShowsQuantities: boolean | undefined,
): boolean | undefined {
  return role === 'readonly' ? readonlyShowsQuantities : true;
}

/** While the settings are unknown a `quantities` entry stays hidden (no link that answers 403). */
export function canSeeNavItem(
  role: Role,
  item: NavItem,
  readonlyShowsQuantities?: boolean,
): boolean {
  if (item.roles && !item.roles.includes(role)) return false;
  return !item.quantities || seesQuantities(role, readonlyShowsQuantities) === true;
}

/** Entries of a role, in menu order. */
export function visibleNavItems(role: Role, readonlyShowsQuantities?: boolean): NavItem[] {
  return NAV_ITEMS.filter((item) => canSeeNavItem(role, item, readonlyShowsQuantities));
}

/**
 * Whether a role may open a page: the restriction of the entry that owns the path, if any. While
 * the settings are unknown a `quantities` page opens (the API checks it again): no flash of
 * "Accesso non consentito" on a direct link.
 */
export function canAccessPath(
  role: Role,
  pathname: string,
  readonlyShowsQuantities?: boolean,
): boolean {
  const owner = NAV_ITEMS.find(
    (item) => (item.roles || item.quantities) && isNavItemActive(pathname, item),
  );
  if (!owner) return true;
  if (owner.roles && !owner.roles.includes(role)) return false;
  return !owner.quantities || seesQuantities(role, readonlyShowsQuantities) !== false;
}

/** Sections of the settings area (tabs under /impostazioni). */
export const SETTINGS_SECTIONS = [
  { key: 'settings', label: 'Generali', to: '/impostazioni/generali' },
  { key: 'points', label: 'Punti', to: '/impostazioni/punti' },
  { key: 'days', label: 'Giorni e orari', to: '/impostazioni/giorni' },
  { key: 'persons', label: 'Persone', to: '/impostazioni/persone' },
  { key: 'users', label: 'Utenti', to: '/impostazioni/utenti' },
  { key: 'movement-types', label: 'Tipi di movimento', to: '/impostazioni/tipi' },
  { key: 'import', label: 'Importazione', to: '/impostazioni/import' },
] as const;
